import React from 'react';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { tomorrow } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { cn } from '../utilsFunction/cn.ts';

interface MarkdownRendererProps {
  content: string;
  className?: string;
}

export const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({ 
  content, 
  className 
}) => {
  // Filter out unwanted content from Executive Summary
  let filteredContent = content;
  if (content.includes('# Executive Summary')) {
    const lines = content.split('\n');
    const filteredLines = lines.filter((line, index) => {
      const trimmedLine = line.trim();
      
      // Skip Vendor Legend section
      if (trimmedLine === '## Vendor Legend' || trimmedLine.startsWith('## Vendor Legend')) {
        return false;
      }
      
      // Skip Key Metrics section
      if (trimmedLine === '## Key Metrics' || trimmedLine.startsWith('## Key Metrics')) {
        return false;
      }
      
      // Skip vendor list lines
      if (trimmedLine.includes('Vendor A:') || trimmedLine.includes('Vendor B:') || trimmedLine.includes('Vendor C:')) {
        return false;
      }
      
      // Skip metrics table lines
      if (trimmedLine.includes('| Metric |') || 
          trimmedLine.includes('|--------|') ||
          trimmedLine.includes('| Recommended Vendor |') ||
          trimmedLine.includes('| Total Cost vs Benchmark |') ||
          trimmedLine.includes('| Items with Benchmark Match |') ||
          trimmedLine.includes('| Overall Risk Level |')) {
        return false;
      }
      
      return true;
    });
    filteredContent = filteredLines.join('\n');
  }

  return (
    <div className={cn("prose-custom", className)}>
      <ReactMarkdown
        components={{
          h1: ({ children }) => (
            <h1 className="text-lg font-semibold text-primary-800 mb-3">
              {children}
            </h1>
          ),
          h2: ({ children }) => (
            <h2 className="text-lg font-semibold text-primary-800 mb-3">
              {children}
            </h2>
          ),
          h3: ({ children }) => (
            <h3 className="text-lg font-medium text-primary-700 mb-2">
              {children}
            </h3>
          ),
          p: ({ children }) => (
            <p className="text-primary-700 leading-relaxed mb-3 text-base">
              {children}
            </p>
          ),
          ul: ({ children }) => (
            <ul className="mb-3 space-y-3">
              {children}
            </ul>
          ),
          ol: ({ children }) => (
            <ol className="mb-3 space-y-1 list-decimal list-inside">
              {children}
            </ol>
          ),
          li: ({ children }) => (
            <li className="text-primary-700 flex items-start gap-2 text-base mb-2">
              <span className="text-accent-500 mt-1">•</span>
              <span>{children}</span>
            </li>
          ),
          strong: ({ children }) => (
            <strong className="font-semibold text-primary-900 text-base">
              {children}
            </strong>
          ),
          em: ({ children }) => (
            <em className="italic text-primary-600 text-base">
              {children}
            </em>
          ),
          blockquote: ({ children }) => (
            <blockquote className="pl-4 italic text-primary-600 bg-accent-50 py-2 rounded-r-lg">
              {children}
            </blockquote>
          ),
          code: ({ children, className }) => {
            const match = /language-(\w+)/.exec(className || '');
            return !match ? (
              <code className="bg-primary-100 text-primary-800 px-2 py-1 rounded text-sm font-mono">
                {children}
              </code>
            ) : (
              <SyntaxHighlighter
                style={tomorrow}
                language={match[1]}
                PreTag="div"
                className="rounded-lg"
              >
                {String(children).replace(/\n$/, '')}
              </SyntaxHighlighter>
            );
          },
          table: ({ children }) => (
            <div className="overflow-x-auto my-6">
              <table className="w-full border-collapse bg-white rounded-lg overflow-hidden shadow-soft">
                {children}
              </table>
            </div>
          ),
          thead: ({ children }) => (
            <thead className="bg-primary-50">
              {children}
            </thead>
          ),
          tbody: ({ children }) => (
            <tbody>
              {children}
            </tbody>
          ),
          tr: ({ children }) => (
            <tr className="hover:bg-primary-50 transition-colors">
              {children}
            </tr>
          ),
          th: ({ children }) => (
            <th className="px-4 py-3 text-left text-sm font-semibold text-primary-800">
              {children}
            </th>
          ),
          td: ({ children }) => (
            <td className="px-4 py-3 text-sm text-primary-700">
              {children}
            </td>
          ),
        }}
      >
        {filteredContent}
      </ReactMarkdown>
    </div>
  );
}; 